"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.DmsTextField = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _TextField = _interopRequireDefault(require("@mui/material/TextField"));
const _excluded = ["name", "label"];
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
/**
 * A wrapper component for the Material-UI `TextField` component.
 *
 * This component provides a convenient way to use the Material-UI `TextField`
 * with additional props for customization.
 *
 * @param {Object} props - The props for the component.
 * @param {string} props.name - The name of the input field.
 * @param {string} props.label - The label of the input field.
 * @param {string} [props.autoComplete] - The autoComplete attribute of the input field.
 * @param {boolean} [props.autoFocus] - If true, the input field will be focused automatically.
 * @param {React.ReactNode} [props.children] - The content inside the text field.
 * @param {Object} [props.classes] - The CSS classes to apply to the component.
 * @param {string} [props.className] - The CSS class name to apply to the component.
 * @param {"primary" | "secondary" | "error" | "info" | "success" | "warning"} [props.color] - The color of the text field.
 * @param {any} [props.defaultValue] - The default value of the input field.
 * @param {boolean} [props.disabled] - If true, the input field will be disabled.
 * @param {boolean} [props.error] - If true, the input field will show an error state.
 * @param {Object} [props.FormHelperTextProps] - Props to pass to the FormHelperText component.
 * @param {boolean} [props.fullWidth] - If true, the input field will take up the full width of its container.
 * @param {React.ReactNode} [props.helperText] - The helper text to display below the input field.
 * @param {string} [props.id] - The id of the input field.
 * @param {Object} [props.InputLabelProps] - Props to pass to the InputLabel component.
 * @param {Object} [props.inputProps] - Props to pass to the input element.
 * @param {Object} [props.InputProps] - Props to pass to the Input component.
 * @param {Function | { current: any }} [props.inputRef] - The ref to the input element.
 * @param {"dense" | "none" | "normal"} [props.margin] - The margin of the input field.
 * @param {number | string} [props.maxRows] - The maximum number of rows for multiline fields.
 * @param {number | string} [props.minRows] - The minimum number of rows for multiline fields.
 * @param {boolean} [props.multiline] - If true, the input field will be multiline.
 * @param {string} [props.placeholder] - The placeholder text to display in the input field.
 * @param {boolean} [props.required] - If true, the input field will be required.
 * @param {number | string} [props.rows] - The number of rows for multiline fields.
 * @param {boolean} [props.select] - If true, the input field will be a select field.
 * @param {Object} [props.SelectProps] - Props to pass to the Select component.
 * @param {"medium" | "small"} [props.size] - The size of the input field.
 * @param {Object} [props.sx] - The system prop that allows defining custom styles.
 * @param {string} [props.type] - The type of the input field.
 * @param {any} [props.value] - The value of the input field.
 * @param {"filled" | "outlined" | "standard"} [props.variant] - The variant of the input field.
 * @returns {JSX.Element} The rendered `TextField` component.
 */
const DmsTextField = _ref => {
  let {
      name,
      label
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/_react.default.createElement(_TextField.default, _extends({
    name: name,
    label: label
  }, props));
};
exports.DmsTextField = DmsTextField;
DmsTextField.propTypes = {
  name: _propTypes.default.string.isRequired,
  label: _propTypes.default.string.isRequired,
  // Material-UI TextField props
  autoComplete: _propTypes.default.string,
  autoFocus: _propTypes.default.bool,
  children: _propTypes.default.node,
  classes: _propTypes.default.object,
  className: _propTypes.default.string,
  color: _propTypes.default.oneOf(["primary", "secondary", "error", "info", "success", "warning"]),
  defaultValue: _propTypes.default.any,
  disabled: _propTypes.default.bool,
  error: _propTypes.default.bool,
  FormHelperTextProps: _propTypes.default.object,
  fullWidth: _propTypes.default.bool,
  helperText: _propTypes.default.node,
  id: _propTypes.default.string,
  InputLabelProps: _propTypes.default.object,
  inputProps: _propTypes.default.object,
  InputProps: _propTypes.default.object,
  inputRef: _propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.shape({
    current: _propTypes.default.any
  })]),
  margin: _propTypes.default.oneOf(["dense", "none", "normal"]),
  maxRows: _propTypes.default.oneOfType([_propTypes.default.number, _propTypes.default.string]),
  minRows: _propTypes.default.oneOfType([_propTypes.default.number, _propTypes.default.string]),
  multiline: _propTypes.default.bool,
  placeholder: _propTypes.default.string,
  required: _propTypes.default.bool,
  rows: _propTypes.default.oneOfType([_propTypes.default.number, _propTypes.default.string]),
  select: _propTypes.default.bool,
  SelectProps: _propTypes.default.object,
  size: _propTypes.default.oneOf(["medium", "small"]),
  sx: _propTypes.default.object,
  type: _propTypes.default.string,
  value: _propTypes.default.any,
  variant: _propTypes.default.oneOf(["filled", "outlined", "standard"])
};